<template>
  <div class="ai-tool-bar-container">
    <div
      ref="aiFloat"
      class="ai-consultant-float"
      :style="aiFloatStyle"
      @mousedown.prevent="onAiDragStart"
      @touchstart.prevent="onAiDragStart"
      @click.capture="onAiClickCapture"
    >
      <Consultant :linkUrl="aiApp.linkUrl">
        <img class="ai-consultant-gif" :src="aiApp.icon" alt="AI顾问" draggable="false" />
      </Consultant>
    </div>
  </div>
</template>

<script>
import Consultant from '@/components/AI/consultant'
import aiGif from '@/assets/gif/ai.gif'

export default {
  name: 'AIToolBar',
  components: {
    Consultant
  },
  data() {
    return {
      aiGif,
      aiFloatX: 0,
      aiFloatY: 0,
      isAiDragging: false,
      aiDragStartClientX: 0,
      aiDragStartClientY: 0,
      aiDragStartX: 0,
      aiDragStartY: 0,
      aiSuppressNextClick: false,
      aiHasMovedDuringDrag: false,
      aiDockedRight: true,
      aiViewportWidth: 0,
      aiViewportHeight: 0,
      aiViewportObserver: null,
      aiViewportRafId: 0,
    }
  },
  computed: {
    aiFloatStyle() {
      return {
        transform: `translate3d(${this.aiFloatX}px, ${this.aiFloatY}px, 0)`
      }
    }
  },
  props: {
    aiApp: {
      type: Object,
      default: () => ({})
    }
  },
  created() {

  },
  mounted() {
    this.initAiFloatPosition()
    this.updateViewportCache()
    this.startViewportObserver()
    window.addEventListener('resize', this.onWindowResize, { passive: true })
  },
  beforeDestroy() {
    this.stopViewportObserver()
    if (this.aiViewportRafId) cancelAnimationFrame(this.aiViewportRafId)
    this.removeAiDragListeners()
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    getViewportSize() {
      const root = document.documentElement
      const width = (root && root.clientWidth) || window.innerWidth || 0
      const height = (root && root.clientHeight) || window.innerHeight || 0
      return { width, height }
    },
    updateViewportCache() {
      const { width, height } = this.getViewportSize()
      this.aiViewportWidth = width
      this.aiViewportHeight = height
    },
    getAiMaxPosition(rect) {
      const { width: viewportWidth, height: viewportHeight } = this.getViewportSize()
      const rightMargin = 0
      const bottomMargin = 16
      const maxX = Math.max(0, viewportWidth - rect.width - rightMargin)
      const maxY = Math.max(0, viewportHeight - rect.height - bottomMargin)
      return { maxX, maxY, viewportHeight }
    },
    onViewportResize() {
      const { width, height } = this.getViewportSize()
      if (width === this.aiViewportWidth && height === this.aiViewportHeight) return
      this.aiViewportWidth = width
      this.aiViewportHeight = height
      if (this.aiViewportRafId) cancelAnimationFrame(this.aiViewportRafId)
      this.aiViewportRafId = requestAnimationFrame(() => {
        this.aiViewportRafId = 0
        this.onWindowResize()
      })
    },
    startViewportObserver() {
      const root = document.documentElement
      if (root && typeof ResizeObserver !== 'undefined' && !this.aiViewportObserver) {
        this.aiViewportObserver = new ResizeObserver(() => {
          this.onViewportResize()
        })
        this.aiViewportObserver.observe(root)
      }
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportResize, { passive: true })
      }
    },
    stopViewportObserver() {
      if (this.aiViewportObserver) {
        this.aiViewportObserver.disconnect()
        this.aiViewportObserver = null
      }
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onViewportResize)
      }
    },
    initAiFloatPosition() {
      this.$nextTick(() => {
        const rect = this.getAiFloatRect()
        const { maxX, maxY, viewportHeight } = this.getAiMaxPosition(rect)

        this.aiFloatX = maxX
        this.aiFloatY = this.clamp(viewportHeight - rect.height - 50 - 180, 0, maxY)
        this.aiDockedRight = true
      })
    },
    onAiClickCapture(event) {
      if (!this.aiSuppressNextClick) return
      this.aiSuppressNextClick = false
      event.preventDefault()
      event.stopPropagation()
    },
    onAiDragStart(event) {
      const point = this.getClientPoint(event)
      if (!point) return

      this.isAiDragging = true
      this.aiHasMovedDuringDrag = false
      this.aiDragStartClientX = point.clientX
      this.aiDragStartClientY = point.clientY
      this.aiDragStartX = this.aiFloatX
      this.aiDragStartY = this.aiFloatY

      this.addAiDragListeners()
    },
    onAiDragMove(event) {
      if (!this.isAiDragging) return
      const point = this.getClientPoint(event)
      if (!point) return
      if (event && event.cancelable) event.preventDefault()

      const deltaX = point.clientX - this.aiDragStartClientX
      const deltaY = point.clientY - this.aiDragStartClientY
      if (!this.aiHasMovedDuringDrag && (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
        this.aiHasMovedDuringDrag = true
      }

      const rect = this.getAiFloatRect()
      const { maxX, maxY } = this.getAiMaxPosition(rect)

      this.aiFloatX = this.clamp(this.aiDragStartX + deltaX, 0, maxX)
      this.aiFloatY = this.clamp(this.aiDragStartY + deltaY, 0, maxY)
    },
    onAiDragEnd() {
      if (!this.isAiDragging) return
      this.isAiDragging = false
      this.removeAiDragListeners()
      if (this.aiHasMovedDuringDrag) this.aiSuppressNextClick = true

      const rect = this.getAiFloatRect()
      const { maxX } = this.getAiMaxPosition(rect)
      this.aiDockedRight = this.aiFloatX >= maxX - 8
      if (this.aiDockedRight) this.aiFloatX = maxX
    },
    onWindowResize() {
      const rect = this.getAiFloatRect()
      const { maxX, maxY } = this.getAiMaxPosition(rect)
      this.aiFloatX = this.aiDockedRight && !this.isAiDragging ? maxX : this.clamp(this.aiFloatX, 0, maxX)
      this.aiFloatY = this.clamp(this.aiFloatY, 0, maxY)
    },
    getAiFloatRect() {
      const el = this.$refs.aiFloat
      if (!el || !el.getBoundingClientRect) return { width: 56, height: 56 }
      return el.getBoundingClientRect()
    },
    getClientPoint(event) {
      if (!event) return null
      if (event.touches && event.touches[0]) return event.touches[0]
      if (typeof event.clientX === 'number' && typeof event.clientY === 'number') return event
      return null
    },
    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    addAiDragListeners() {
      window.addEventListener('mousemove', this.onAiDragMove)
      window.addEventListener('mouseup', this.onAiDragEnd)
      window.addEventListener('touchmove', this.onAiDragMove, { passive: false })
      window.addEventListener('touchend', this.onAiDragEnd)
      window.addEventListener('touchcancel', this.onAiDragEnd)
    },
    removeAiDragListeners() {
      window.removeEventListener('mousemove', this.onAiDragMove)
      window.removeEventListener('mouseup', this.onAiDragEnd)
      window.removeEventListener('touchmove', this.onAiDragMove)
      window.removeEventListener('touchend', this.onAiDragEnd)
      window.removeEventListener('touchcancel', this.onAiDragEnd)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-tool-bar-container {
  pointer-events: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
}

.ai-consultant-float {
  pointer-events: auto;
  position: fixed;
  top: 0;
  left: 0;
  border-radius: 20px;
  background: #fff;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  justify-content: center;
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.ai-consultant-float:active {
  cursor: grabbing;
}

.ai-consultant-gif {
  width: 56px;
  height: 56px;
  object-fit: contain;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}
</style>
